"use client";

/**
 * @file 宠物拟人化页面组件
 * @description 提供宠物照片上传和拟人化处理功能的页面
 * 使用"use client"指令标记为客户端组件，启用交互功能
 */

import { useState } from 'react';
import ImageUploader from '@/components/features/pet-to-human/ImageUploader';
import StyleSelector from '@/components/features/pet-to-human/StyleSelector';
import ResultDisplay from '@/components/features/pet-to-human/ResultDisplay';
import Button from '@/components/common/Button';

// 定义风格选项
const styleOptions = [
  { id: 'professional', name: 'Professional', description: 'Formal business or professional appearance' },
  { id: 'casual', name: 'Casual', description: 'Everyday casual dressed character' },
  { id: 'fantasy', name: 'Fantasy', description: 'Character with fantasy elements' },
  { id: 'vintage', name: 'Vintage', description: 'Character in different historical period styles' },
  { id: 'cartoon', name: 'Cartoon', description: 'Anime or cartoon style character' },
];

/**
 * 宠物拟人化页面组件
 * @returns {JSX.Element} 宠物拟人化页面
 */
export default function PetToHumanPage() {
  // 状态管理
  const [uploadedImage, setUploadedImage] = useState<string | null>(null);
  const [selectedStyle, setSelectedStyle] = useState(styleOptions[0]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [resultImage, setResultImage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  /**
   * 处理图片上传
   * @param {string} imageDataUrl - 上传图片的数据URL
   */
  const handleImageUpload = (imageDataUrl: string) => {
    setUploadedImage(imageDataUrl);
    setResultImage(null); // 重置结果
    setError(null); // 重置之前的错误
  };

  /**
   * 处理风格选择
   * @param {object} style - 选择的风格对象
   */
  const handleStyleSelect = (style: typeof styleOptions[0]) => {
    setSelectedStyle(style);
  };

  /**
   * 使用Gemini API处理图片转换请求
   */
  const handleProcessImage = async () => {
    if (!uploadedImage) return;

    setIsProcessing(true);
    setError(null);

    try {
      // 调用Gemini转换API
      const response = await fetch('/api/transform/gemini', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          image: uploadedImage,
          style: selectedStyle,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.details || errorData.error || '图片处理失败');
      }

      const data = await response.json();

      if (data.success && data.resultImage) {
        setResultImage(data.resultImage);
      } else {
        throw new Error('API响应无效');
      }
    } catch (error) {
      console.error('图片转换错误:', error);
      setError(error instanceof Error ? error.message : '发生未知错误');
      setResultImage(null);
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <div className="container mx-auto px-4 py-8">
      {/* 页面标题 */}
      <div className="text-center mb-10">
        <h1 className="text-3xl font-bold mb-2">Pet to Human Transformation</h1>
        <p className="text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
          Upload your pet photo and our AI will transform it into a human portrait. Choose your preferred style and see your pet in a whole new way.
        </p>
        <div className="mt-2 text-sm text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/20 p-2 rounded-md inline-block">
          Free usage limits: 10 transformations per IP per day, maximum 3 requests per minute
        </div>
      </div>

      {/* 主要内容区域 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 max-w-6xl mx-auto">
        {/* 左侧：上传和设置 */}
        <div className="space-y-6">
          {/* 图片上传组件 */}
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md p-6">
            <h2 className="text-xl font-semibold mb-4">1. Upload Pet Photo</h2>
            <ImageUploader onImageUpload={handleImageUpload} />
          </div>

          {/* 风格选择组件 */}
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md p-6">
            <h2 className="text-xl font-semibold mb-4">2. Choose Transformation Style</h2>
            <StyleSelector
              options={styleOptions}
              selectedStyle={selectedStyle}
              onSelectStyle={handleStyleSelect}
            />
          </div>

          {/* 处理按钮 */}
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md p-6">
            <h2 className="text-xl font-semibold mb-4">3. Start Transformation</h2>
            <Button
              onClick={handleProcessImage}
              disabled={!uploadedImage || isProcessing}
              className="w-full"
              size="lg"
            >
              {isProcessing ? 'Processing...' : 'Start Transformation'}
            </Button>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
              Processing may take up to 30 seconds, please be patient
            </p>

            {/* 显示错误信息（如果有） */}
            {error && (
              <div className="mt-4 p-3 bg-red-50 text-red-700 dark:bg-red-900/20 dark:text-red-400 rounded-lg text-sm">
                <strong>Error:</strong> {error}
              </div>
            )}
          </div>
        </div>

        {/* 右侧：结果显示 */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md p-6 h-full">
          <h2 className="text-xl font-semibold mb-4">Transformation Result</h2>
          <ResultDisplay
            originalImage={uploadedImage}
            resultImage={resultImage}
            isProcessing={isProcessing}
            selectedStyle={selectedStyle}
          />
        </div>
      </div>

      {/* 工作原理部分 */}
      <div className="mt-16 max-w-4xl mx-auto">
        <h2 className="text-2xl font-bold mb-6 text-center">How Does Pet to Human Work?</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md p-6">
            <div className="text-blue-500 text-xl font-bold mb-2">1</div>
            <h3 className="font-semibold mb-2">Feature Analysis</h3>
            <p className="text-gray-600 dark:text-gray-400">
              Our AI analyzes your pet&apos;s facial features, fur color, posture, and expressions to capture their unique personality.
            </p>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md p-6">
            <div className="text-blue-500 text-xl font-bold mb-2">2</div>
            <h3 className="font-semibold mb-2">Style Matching</h3>
            <p className="text-gray-600 dark:text-gray-400">
              The system transforms your pet&apos;s characteristics into corresponding human features and clothing styles based on your selection.
            </p>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md p-6">
            <div className="text-blue-500 text-xl font-bold mb-2">3</div>
            <h3 className="font-semibold mb-2">Portrait Generation</h3>
            <p className="text-gray-600 dark:text-gray-400">
              Using Google&apos;s Gemini AI technology, a human portrait that preserves your pet&apos;s personality is generated, allowing you to appreciate your pet in a whole new way.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
